import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {ArrUtil} from 'yougile-util'
import {AvatarBase} from '@components/common/base/avatar'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {noPropagate} from '@containers/hooks'
import {Checkbox} from '../checkbox'
import IconChecked from './icon-checked.svg'
import IconDot from './icon-dot.svg'
import {MenuItem, MenuListProps, TwListItemProps} from './types'
import {getAllState, getSelected} from './utils'

const TwListItem = tw.div<TwListItemProps>`
  flex
  items-center
  justify-between
  cursor-pointer
  ${p => (p.$large ? 'p-12 h-40' : 'px-12 py-8 h-32')}
  ${p =>
    p.$disabled
      ? 'pointer-events-none text-disabled'
      : `hover:bg-interactive-l100 active:bg-interactive-l80 text-primary`} 
`

const TwListItemAll = tw.div<TwListItemProps>`
  flex
  items-center
  gap-8
  cursor-pointer
  ${p => (p.$large ? 'p-12 h-40' : 'px-12 py-8 h-32')}
  ${p =>
    p.$disabled
      ? 'pointer-events-none text-disabled'
      : `hover:bg-interactive-l100 active:bg-interactive-l80 text-primary`} 
`

const TwList = tw.div`
  inline-flex
  py-4
  flex-col
  text-14
  cursor-default
  select-none
  max-h-380
`

const TwIconChecked = tw.div<{$disabled: boolean}>`
  shrink-0
  ml-8 
  flex
  ${p => (p.$disabled ? 'text-disabled' : 'text-action-default')} 
`

const TwIconLeft = tw.div<{$disabled: boolean}>`
  flex
  mr-8
  shrink-0
  ${p => (p.$disabled ? 'text-disabled' : 'text-secondary')} 
`

const TwWrap = tw.div`
  flex 
  items-center
`

const TwDot = tw.span`
  text-success
  flex
  shrink-0
  self-center
  mr-8
`

const TwAvatar = tw.span`
  flex
  shrink-0
  self-center
  mr-8
`

export const MenuList = <T,>(props: MenuListProps<T>) => {
  const {items, className} = props
  const {multi = false, withoutAll = true} = props

  return (
    <WithPerfectScrollbar>
      {!withoutAll && multi && <SelectAll<T> {...props} />}
      <TwList className={className}>
        {items.map((item, i) => (
          <ListItem<T> item={item} {...props} key={i} />
        ))}
      </TwList>
    </WithPerfectScrollbar>
  )
}

const SelectAll = <T,>(props: MenuListProps<T>) => {
  const {items, value, onChange, size} = props
  const {allText = Lang.msg.component.dropdown.all} = props
  const large = size === 'big'
  const allState = getAllState(value, items)

  return (
    <TwListItemAll
      onClick={e => {
        const newValue = allState ? [] : items.map(x => x.value)
        noPropagate(e)
        onChange?.(newValue)
      }}
      $large={large}
      $disabled={false}
    >
      <Checkbox value={allState} />
      <span>{allText}</span>
    </TwListItemAll>
  )
}

interface Props<T> extends MenuListProps<T> {
  item: MenuItem<T>
}

const ListItem = <T,>(props: Props<T>) => {
  const {items, item, value, onChange, size} = props
  const {multi = false} = props
  const {iconChecked, value: itemValue, label} = item
  const {disabled = false, icon, dot, user} = item
  const isSelected = getSelected(value, itemValue, multi)
  const iconCheckedShow = iconChecked && isSelected
  const large = size === 'big'
  const isMulti = multi && Array.isArray(value)
  return (
    <TwListItem
      $large={large}
      $disabled={disabled}
      onClick={e => {
        noPropagate(e)
        if (isMulti) {
          const newValueSet = new Set(ArrUtil.xor<T>(value, [itemValue]))
          const newValue = ArrUtil.filterMap(items, x =>
            newValueSet.has(x.value) ? x.value : null
          )
          onChange?.(newValue)
        } else {
          onChange?.(itemValue)
        }
      }}
    >
      <TwWrap>
        {multi && (
          <TwAvatar>
            <Checkbox value={isSelected} disabled={disabled} />
          </TwAvatar>
        )}
        {dot && (
          <TwDot>
            <IconDot />
          </TwDot>
        )}
        {user && (
          <TwAvatar>
            <AvatarBase user={user} hideStatus hideOutline size={16} />
          </TwAvatar>
        )}
        {icon && (
          <TwIconLeft $disabled={disabled}>
            <IconTabler customSize={16} icon={icon} viewBox={'0 0 24 24'} />
          </TwIconLeft>
        )}
        <span className="truncate">{label}</span>
      </TwWrap>
      {iconCheckedShow && (
        <TwIconChecked $disabled={disabled}>
          <IconChecked />
        </TwIconChecked>
      )}
    </TwListItem>
  )
}
